import React from "react";
import { Alert } from 'react-bootstrap'
import { IQuoteStepState } from "../../components/interfaces";
import { IAppState } from "../../state/interfaces";
import { connect } from "react-redux";
import { FULL_QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { ValidatableForm } from "../../components/ValidatableForm"
import { ValidatableFormGroup } from "../../components/ValidatableFormGroup"
import { IDriverStepProps } from "../../components/interfaces";
import Utilities from "../../classes/Utilities";
import { validationUtils } from "../../validation/Validation";
import { QuoteStep } from "./QuoteStepLayout";
import ButtonContinue from "../../components/ButtonContinue";
import StateList from "../../components/StateList";
import QuoteStepBase from '../../components/QuoteStepBase';
import { IDriver, IQuoteApiClient, ILOBApiClient, IPaths } from "../../classes/Domain";
import { IUpdateDriverResult } from "../../classes/ExternalQuote";
import { Rifm } from 'rifm';
import QuoteActions from "../../state/appActions";
import FullQuoteActions from "../../state/fullQuoteActions";
import _ from "lodash";

interface IAppStateUpdate {
    quoteId: number,
    paymentPlanCode: number,
    driver: IDriver;
}

interface IState extends IQuoteStepState {
    updateResult: IUpdateDriverResult | null,
    driver: IDriver | null;
}

interface IProps extends IDriverStepProps {
    quoteId: number,
    paymentPlanCode: number,
    reRated: boolean,
    driversCount: number,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class Driver extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);

        this.state = {
            driver: null,
            updatingStore: false,
            errors: null,
            updateResult: null
        };
    }

    componentDidMount() {
        this.props.reportCurrentStep();

        this.setState({ ...this.state, updatingStore: false, errors: null });

        validationUtils.subscribeToValidationFailure(FULL_QUOTE_STEPS.DRIVERS,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, errors: validationData });
            });
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(FULL_QUOTE_STEPS.DRIVERS);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            const driver = { ...nextProps.driver };
            nextProps.meta.propsSource = "";

            if ("undefined" === typeof driver.defensiveDriver || null === driver.defensiveDriver) {
                driver.defensiveDriver = false;
            }

            return {
                ...prevState,
                driver
            };
        }

        return prevState;
    }

    getNextUrl() {
        const lastDriver = this.props.driverIndex === this.props.driversCount - 1;
        return lastDriver
            ? Utilities.formatUrl(this.props.PATHS.FQ_COVERAGES, "")
            : Utilities.formatUrl(this.props.PATHS.FQ_DRIVERS, (this.props.driverIndex + 1).toString());
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {
            let driver = { ...this.state.driver, isInsured: 0 === this.props.driverIndex } as IDriver;

            driver.firstName = driver.firstName.trim();
            driver.lastName = driver.lastName.trim();

            const updatedDriver = {
                ...this.props.driver,
                ...driver
            };

            this.props.updateAppState({
                quoteId: this.props.quoteId,
                paymentPlanCode: this.props.paymentPlanCode,
                driver: updatedDriver
            })
            .then((response) => {
                this.setState({ updateResult: response.data }, () => {
                    if (!this.state.updateResult?.isSuccess) {
                        this.setState({ ...this.state, updatingStore: false, errors: null });
                    }
                    else {
                        this.setState({ updatingStore: false, errors: null }, () => this.props.history.push(this.getNextUrl()));
                    }
                });
            },
            () => this.setState({ ...this.state, updatingStore: false }));
        });
    }

    render() {
        return (
            <QuoteStep id="driver">
                <ValidatableForm hasErrors={null != this.state.errors}>
                    <div className="titleArea">
                            {
                                this.props.driverIndex === 0
                                ?
                                <h5>
                                    Insured Info
                                </h5>
                                :
                                <h5>
                                    Driver #{this.props.driverIndex + 1} Info
                                </h5>
                            }
                    </div>

                    {
                        this.state.updateResult && !this.state.updateResult.isSuccess && this.state.updateResult.errors
                            ? <Alert variant={"danger"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review errors below</p>
                                <ul>
                                    {
                                        this.state.updateResult.errors.map((error) => (
                                            <li key={Utilities.uniqueId()}>
                                                <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </Alert>
                            : null
                    }

                    <p className="info">We need to gather some final details about the drivers to verify we have everything correct:</p>
                                        
                    <ValidatableFormGroup
                        label="First Name"
                        value={this.getValueSafe(this.state.driver, "firstName")}
                        placeholder="First Name"
                        validated={null !== this.state.errors}
                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "firstName")}
                        onChange={(e) => { this.setStateValue(e.target.value, "firstName", this.state.driver) }} />

                    <ValidatableFormGroup
                        label="Last Name"
                        value={this.getValueSafe(this.state.driver, "lastName")}
                        placeholder="Last Name"
                        validated={null !== this.state.errors}
                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "lastName")}
                        onChange={(e) => { this.setStateValue(e.target.value, "lastName", this.state.driver) }} />

                    <ValidatableFormGroup
                        label="License Number"
                        className="col-left"
                        value={this.getValueSafe(this.state.driver, "licenseNumber")}
                        placeholder="License Number"
                        validated={null !== this.state.errors}
                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "licenseNumber")}
                        onChange={(e) => { this.setStateValue(e.target.value, "licenseNumber", this.state.driver) }} />

                    <StateList
                        label="License State"
                        className="col-right"
                        value={this.getValueSafe(this.state.driver, "licenseState")}
                        validated={null !== this.state.errors}
                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "licenseState")}
                        onChange={(state) => { this.setStateValue(state?.value ?? "", "licenseState", this.state.driver) }} />

                    <Rifm
                        accept={/\d/g}
                        mask={!!this.state.driver && 10 <= this.state.driver.birthDate.length}
                        format={Utilities.formatDate}
                        value={this.getValueSafe(this.state.driver, "birthDate")}
                        onChange={(v) => { this.setStateValue(v, "birthDate", this.state.driver) }}>
                        {({ value, onChange }) => (
                            <ValidatableFormGroup
                                label="Birthdate"
                                placeholder="mm/dd/yyyy"
                                value={value}
                                validated={null !== this.state.errors}
                                validationError={validationUtils.getValidationErrorFor(this.state.errors, "birthDate")}
                                onChange={(e) => { onChange(e) }}
                                pattern="[0-9]*"
                                inputmode="numeric"
                                autoComplete="bday"
                                name="birthdate"
                            />
                        )}
                    </Rifm>

                    {/*<Question*/}
                    {/*    name="defensiveDriver"*/}
                    {/*    selected={(v) => { this.setStateValue(v === QUOTE_QUESTIONS.YES_NO.YES ? true : false, "defensiveDriver", this.state.driver) }}*/}
                    {/*    selectedValue={this.state.driver?.defensiveDriver ? QUOTE_QUESTIONS.YES_NO.YES : QUOTE_QUESTIONS.YES_NO.NO }*/}
                    {/*    error={validationUtils.getValidationErrorFor(this.state.errors, "defensiveDriver")}*/}
                    {/*    items={[*/}
                    {/*        QUOTE_QUESTIONS.YES_NO.YES,*/}
                    {/*        QUOTE_QUESTIONS.YES_NO.NO*/}
                    {/*    ]}*/}
                    {/*    lable={this.props.driverIndex === 0*/}
                    {/*        ? "Are you eligible for Defensive Driver Discount?"*/}
                    {/*        : 'Are ' + this.state.driver?.firstName.toUpperCase() + ' ' + this.state.driver?.lastName.toUpperCase() + ' eligible for Defensive Driver Discount?'}*/}
                    {/*    tooltip={{ title: "Defensive Driver", content: "Need proof of current defensive driver course completion." } as IToolTipOption}*/}
                    {/*/>*/}

                    <div className="btnContainer">
                        <ButtonContinue
                            disabled={this.state.updatingStore}
                            showLoading={this.state.updatingStore}
                            onClick={this.next} />
                    </div>
                </ValidatableForm>
            </QuoteStep>

        );
    }
}

let mapProps = (state: IAppState, ownProps: IDriverStepProps) => {
    var driverIndex = !ownProps.match.params["driverIndex"]
        ? 0
        : parseInt(ownProps.match.params["driverIndex"], 10);

    var drivers = _.filter(state.externalQuote?.drivers, (d) => !d.fromVerisk);

    return {
        quoteId: state.externalQuote?.rtrQuoteId,
        paymentPlanCode: state.externalQuote?.paymentPlanCode,
        reRated: state.externalQuote?.reRated,
        driverIndex: driverIndex,
        driversCount: drivers.length,
        driver: drivers[driverIndex],
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.DRIVERS)),
        updateAppState: (update: IAppStateUpdate) => dispatch(fullQuoteActions.updateDriver(update.quoteId, update.paymentPlanCode, update.driver))
    };
};

export default connect(mapProps, mapDispatch)(Driver);